import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { whatsappLink, scrollToId } from "../config/site";
import { useLang } from "../config/i18n";
import { RitualSeal } from "./RitualSeal";

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.25 + i * 0.18, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskedLine = ({ i, children, className }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      custom={i}
      variants={lineVariants}
      initial="hidden"
      animate="show"
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLang();
  const { scrollY } = useScroll();
  const yEmblem = useTransform(scrollY, [0, 900], [0, 120]);

  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-[110px] pb-16"
    >
      <RitualSeal className="absolute -left-72 top-1/2 -translate-y-1/2 w-[840px] text-[#C5A059] opacity-[0.06] pointer-events-none" />
      <div className="absolute right-[-10%] top-1/2 -translate-y-1/2 w-[60vw] h-[60vw] max-w-[780px] max-h-[780px] bg-[radial-gradient(circle,rgba(217,169,74,0.13),transparent_62%)] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 w-full grid lg:grid-cols-[1.15fr_1fr] gap-14 lg:gap-10 items-center">
        <div>
          <motion.p
            key={t.hero.overline}
            data-testid="hero-overline"
            initial={{ opacity: 0, letterSpacing: "0.5em" }}
            animate={{ opacity: 1, letterSpacing: "0.28em" }}
            transition={{ duration: 1.2, delay: 0.1 }}
            className="text-[10px] sm:text-xs font-semibold text-[#C5A059] uppercase mb-7"
          >
            {t.hero.overline}
          </motion.p>

          <h1 data-testid="hero-title" className="font-heading font-light text-[#F7F7F8] leading-[1.05]">
            <MaskedLine i={0} className="text-4xl sm:text-6xl lg:text-7xl tracking-wide">
              {t.hero.title1}
            </MaskedLine>
            <MaskedLine i={1} className="text-5xl sm:text-7xl lg:text-8xl tracking-wide gold-text">
              {t.hero.title2}
            </MaskedLine>
          </h1>

          <div className="overflow-hidden mt-7">
            <motion.p
              data-testid="hero-subtitle"
              custom={2}
              variants={lineVariants}
              initial="hidden"
              animate="show"
              className="text-base sm:text-lg text-[#F7F7F8] font-light"
            >
              {t.hero.tagline}
            </motion.p>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95 }}
            className="mt-5 max-w-xl text-sm sm:text-[15px] text-[#A3A3AF] leading-relaxed font-light"
          >
            {t.hero.text}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              data-testid="hero-cta-whatsapp"
              href={whatsappLink(t.waDefault)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold"
            >
              {t.hero.cta1} <ArrowRight size={15} />
            </a>
            <button
              data-testid="hero-cta-works"
              onClick={() => scrollToId("trabalhos")}
              className="btn-outline-gold"
            >
              {t.hero.cta2}
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.45 }}
            className="mt-8 flex items-center gap-2.5 text-xs text-[#6E6E7D] font-light"
          >
            <span className="text-[#D9A94A] text-[9px]">◆</span>
            {t.hero.note}
          </motion.p>
        </div>

        <motion.div
          style={{ y: yEmblem }}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative flex justify-center"
        >
          <div className="emblem-float relative">
            <div className="absolute -inset-6 rounded-full bg-[radial-gradient(circle,rgba(217,169,74,0.22),transparent_68%)] pointer-events-none" />
            <img
              data-testid="hero-emblem-img"
              src="/selo-mestre-jonas.png"
              alt="Selo de Alta Magia — Mestre Jonas Barcellos"
              className="relative w-full max-w-[300px] sm:max-w-[380px] lg:max-w-[430px] rounded-full object-cover border border-[#C5A059]/40 shadow-[0_0_90px_-10px_rgba(217,169,74,0.35)]"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
